import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Work from "@/pages/Work";
import Project from "@/pages/Project";
import Custom from "@/pages/Custom";
import Start from "@/pages/Start";
import Purchase from "@/pages/Purchase";
import Onboard from "@/pages/Onboard";
import Admin from "@/pages/Admin";
import ManagePurchase from "@/pages/ManagePurchase";
import Legal from "@/pages/Legal";

const EASE = [0.16, 1, 0.3, 1];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Reset through Lenis itself — a raw window.scrollTo fights Lenis's
    // internal scroll position and causes a bounce back down the page.
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true, force: true });
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [pathname]);
  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.main
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.45, ease: EASE }}
      >
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/work" element={<Work />} />
          <Route path="/work/:slug" element={<Project />} />
          <Route path="/custom" element={<Custom />} />
          <Route path="/start" element={<Start />} />
          <Route path="/purchase/success" element={<Onboard />} />
          <Route path="/onboard/:token" element={<Onboard />} />
          <Route path="/purchase/:slug" element={<Purchase />} />
          <Route path="/manage" element={<ManagePurchase />} />
          <Route path="/privacy" element={<Legal doc="privacy" />} />
          <Route path="/terms" element={<Legal doc="terms" />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </motion.main>
    </AnimatePresence>
  );
}

function App() {
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;
    // syncTouch: false — touch swipes stay fully native; Lenis only smooths
    // wheel/trackpad input, so the scroll-driven hero never fights a finger.
    const lenis = new Lenis({ autoRaf: true, lerp: 0.09, syncTouch: false });
    window.__lenis = lenis;
    return () => {
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App grain page-shell" data-testid="app-shell">
      <BrowserRouter>
        <ScrollToTop />
        <Navbar />
        <AnimatedRoutes />
        <Footer />
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-center" />
    </div>
  );
}

export default App;
